/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  getDay, // Import getDay from date-fns
  isWithinInterval,
  parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale'; // Import Spanish locale

import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  LocationOn as LocationOnIcon,
  EventBusy as EventBusyIcon, // Icon for unavailable
} from '@mui/icons-material';
import { getMonthlyDoctorAvailabilityAndExceptions } from '../services/api_service';
import { useAlert } from '../AlertContext';

const getDayLabels = (locale, weekStartsOn) => {
  const dayLabels = [];
  const someDate = new Date();
  const start = startOfWeek(someDate, { locale, weekStartsOn });
  for (let i = 0; i < 7; i++) {
    dayLabels.push(format(addDays(start, i), 'EEE', { locale })); // EEE for short day name (e.g., Lun)
  }
  return dayLabels;
};

export default function DoctorAvailabilityCalendar({ doctorId, doctorName }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [rawAvailabilityData, setRawAvailabilityData] = useState({
    recurringAvailability: [],
    exceptions: [],
  });
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();

  useEffect(() => {
    if (doctorId) {
      fetchMonthData(currentMonth);
    }
  }, [currentMonth, doctorId]);

  const fetchMonthData = async (month) => {
    setLoading(true);
    const startDate = format(
      startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
      'yyyy-MM-dd'
    );
    const endDate = format(
      endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
      'yyyy-MM-dd'
    );

    try {
      const response = await getMonthlyDoctorAvailabilityAndExceptions(
        doctorId,
        startDate,
        endDate
      );
      setRawAvailabilityData({
        recurringAvailability: response.recurringAvailability || [],
        exceptions: response.exceptions || [],
      });
    } catch (error) {
      console.error('Error fetching monthly availability data:', error);
      showAlert('Error al cargar la disponibilidad mensual', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Conflict resolution logic moved to frontend
  const resolveEffectiveAvailability = useMemo(() => {
    const resolved = {};
    const { recurringAvailability, exceptions } = rawAvailabilityData;

    const monthStart = startOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });

    let dayIterator = calendarStart;
    while (dayIterator <= calendarEnd) {
      const formattedDate = format(dayIterator, 'yyyy-MM-dd');
      const jsDayOfWeek = getDay(dayIterator); // 0=Sun, 1=Mon, ..., 6=Sat
      const dbDayOfWeek = jsDayOfWeek === 0 ? 7 : jsDayOfWeek; // 1=Mon, ..., 7=Sun

      let effectiveSlots = [];

      // 1. Get base recurring availability for the day
      recurringAvailability.forEach((rec) => {
        const recStart = rec.start_date ? parseISO(rec.start_date) : null;
        const recEnd = rec.end_date ? parseISO(rec.end_date) : null;

        if (
          Number(rec.day_of_week) === dbDayOfWeek &&
          (recStart === null ||
            isWithinInterval(dayIterator, {
              start: recStart,
              end: recEnd || dayIterator,
            })) &&
          (recEnd === null ||
            isWithinInterval(dayIterator, {
              start: recStart || dayIterator,
              end: recEnd,
            }))
        ) {
          effectiveSlots.push({
            hstart: rec.hstart,
            hend: rec.hend,
            unit: rec.unit,
          });
        }
      });

      // Sort effective slots by start time
      effectiveSlots.sort((a, b) => a.hstart.localeCompare(b.hstart));

      // 2. Apply exceptions for the specific date
      const dayExceptions = exceptions.filter((exc) => {
        return exc.exception_date.split('T')[0] === formattedDate;
      });

      for (const exception of dayExceptions) {
        if (exception.exception_type === 'unavailable_full_day') {
          effectiveSlots = []; // Override everything
          break;
        } else if (exception.exception_type === 'available_override') {
          effectiveSlots = exception.time_slots || []; // This defines availability
          break; // Stop processing other exceptions for this day
        } else if (exception.exception_type === 'unavailable_partial_day') {
          const unavailableSlots = exception.time_slots || [];
          let filteredSlots = [];
          for (const availSlot of effectiveSlots) {
            let isOverlapping = false;
            for (const unavailSlot of unavailableSlots) {
              // Check for overlap: [start1, end1) and [start2, end2) overlap if start1 < end2 AND end1 > start2
              if (
                availSlot.hstart < unavailSlot.hend &&
                unavailSlot.hstart < availSlot.hend
              ) {
                isOverlapping = true;
                break;
              }
            }
            if (!isOverlapping) {
              filteredSlots.push(availSlot);
            }
          }
          effectiveSlots = filteredSlots;
        }
      }

      resolved[formattedDate] = effectiveSlots;
      dayIterator = addDays(dayIterator, 1);
    }

    return resolved;
  }, [rawAvailabilityData, currentMonth]); // Re-calculate when raw data or month changes

  const renderHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          p: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
        }}
      >
        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ArrowBackIosNewIcon sx={{ color: 'primary.contrastText' }} />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          {format(currentMonth, 'MMMM yyyy', { locale: es })}
        </Typography>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ArrowForwardIosIcon sx={{ color: 'primary.contrastText' }} />
        </IconButton>
      </Box>
    );
  };

  const renderDays = () => {
    const weekDayLabels = getDayLabels(es, 1); // Get labels for week starting Monday (1)
    return (
      <Grid container>
        {weekDayLabels.map((dayLabel) => (
          <Grid
            item
            xs={12 / 7}
            key={dayLabel}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <Typography variant="caption" fontWeight="bold">
              {dayLabel}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Always a Monday
    const endDate = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });

    const allDays = [];
    let day = startDate;

    while (day <= endDate) {
      allDays.push(day);
      day = addDays(day, 1);
    }

    const dayCells = allDays.map((dayOfMonth) => {
      const formattedDate = format(dayOfMonth, 'yyyy-MM-dd');
      const effectiveAvailabilityForDay =
        resolveEffectiveAvailability[formattedDate] || [];
      const isCurrentMonth = isSameMonth(dayOfMonth, currentMonth);
      const isToday = isSameDay(dayOfMonth, new Date());

      return (
        <Grid
          item
          xs={12 / 7}
          key={formattedDate} // Using formattedDate as key
          sx={{
            height: 120, // Fixed height for each day cell
            border: '1px solid',
            borderColor: 'grey.300',
            p: 0.5,
            overflowY: 'auto', // Scroll for many slots
            bgcolor: isToday
              ? 'info.light'
              : isCurrentMonth
                ? 'white'
                : 'grey.100',
            opacity: isCurrentMonth ? 1 : 0.6,
          }}
        >
          <Typography
            variant="caption"
            align="right"
            display="block"
            color={isCurrentMonth ? 'text.primary' : 'text.secondary'}
            fontWeight={isToday ? 'bold' : 'normal'}
          >
            {format(dayOfMonth, 'd')}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {effectiveAvailabilityForDay.length > 0
              ? effectiveAvailabilityForDay.map((slot, index) => (
                  <Chip
                    key={index}
                    label={`${slot.hstart.substring(0, 5)} - ${slot.hend.substring(0, 5)}`}
                    size="small"
                    color="success"
                    sx={{ mb: 0.5, mr: 0.5 }}
                    variant="outlined"
                    icon={<LocationOnIcon sx={{ fontSize: 12 }} />}
                    title={`Unidad: ${slot.unit}`}
                  />
                ))
              : isCurrentMonth && (
                  <Chip
                    label="No disponible"
                    size="small"
                    color="error"
                    variant="outlined"
                    icon={<EventBusyIcon sx={{ fontSize: 12 }} />}
                  />
                )}
          </Box>
        </Grid>
      );
    });

    const weeks = [];
    for (let i = 0; i < dayCells.length; i += 7) {
      weeks.push(
        <Grid container key={`week-${i}`} sx={{ flexGrow: 1 }}>
          {dayCells.slice(i, i + 7)}
        </Grid>
      );
    }

    return <Box sx={{ flexGrow: 1 }}>{weeks}</Box>;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }} gutterBottom>
          Disponibilidad de {doctorName}
        </Typography>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {renderHeader()}
            {renderDays()}
            {renderCells()}
          </Paper>
        )}
      </CardContent>
    </Card>
  );
}
